/**
 * Скачивание вложений чата (фото / видео / файлы) — одна функция для всех
 * мест: контекстное меню сообщения, кнопка «Скачать» под видео, лайтбоксы.
 *
 * ПК: <a download> на URL с ?download=1 — backend отдаёт
 * Content-Disposition: attachment, браузер сам показывает загрузку.
 *
 * Телефон (узкий вьюпорт): Web Share API с файлом — на iOS открывается
 * системный лист с «Сохранить видео» / «Сохранить в Файлы». Для этого файл
 * сначала целиком тянется в память, поэтому показываем тост с прогрессом
 * («Скачиваю … 37%»): раньше 37-мегабайтное видео 10+ секунд выглядело как
 * «ничего не происходит». Очень большие файлы (>80 МБ) и отказ share —
 * обычная загрузка ссылкой.
 */
import { chatAttachmentUrl } from "@/api/dashboard";

const SHARE_MAX_BYTES = 80 * 1024 * 1024;

/** Прямая ссылка на скачивание (форс attachment, не inline). */
export function chatAttachmentDownloadUrl(attachmentId: number): string {
  return `${chatAttachmentUrl(attachmentId)}?download=1`;
}

/** id вложения из URL вида …/chat/attachments/123/download. */
export function attachmentIdFromUrl(url: string | null | undefined): number | null {
  if (!url) return null;
  const m = /\/chat\/attachments\/(\d+)\//.exec(url);
  return m ? Number(m[1]) : null;
}

function withDownloadFlag(url: string): string {
  if (/[?&]download=1(&|$)/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}download=1`;
}

function anchorDownload(url: string, filename: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function fmtMb(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} МБ`;
}

function shortName(name: string, max = 28): string {
  return name.length > max ? `${name.slice(0, max - 1)}…` : name;
}

/* Тост прогресса — vanilla DOM (как chatVanillaMenu), чтобы не тянуть React
 * в helper и показывать его из любого места: меню, лайтбокс, пузырь. */
type Toast = { update: (text: string) => void; hide: (delayMs: number) => void };
function showToast(text: string): Toast {
  let el = document.getElementById("kb-dl-toast") as HTMLDivElement | null;
  if (!el) {
    el = document.createElement("div");
    el.id = "kb-dl-toast";
    el.setAttribute("role", "status");
    el.style.cssText = [
      "position:fixed", "left:50%", "bottom:calc(92px + env(safe-area-inset-bottom, 0px))",
      "transform:translateX(-50%)", "max-width:min(92vw, 440px)", "padding:10px 16px",
      "border-radius:14px", "background:rgba(20,20,20,0.92)", "color:#fff",
      "font-family:inherit", "font-size:13px", "font-weight:500", "line-height:1.35",
      "z-index:12000", "box-shadow:0 8px 24px rgba(0,0,0,0.35)", "white-space:nowrap",
      "overflow:hidden", "text-overflow:ellipsis", "pointer-events:none",
    ].join(";");
    document.body.appendChild(el);
  }
  el.textContent = text;
  let timer: number | null = null;
  return {
    update(t: string) { if (el) el.textContent = t; },
    hide(delayMs: number) {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => { el?.remove(); el = null; }, delayMs);
    },
  };
}

async function fetchWithProgress(
  url: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<Blob> {
  const resp = await fetch(url, { credentials: "include" });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const type = resp.headers.get("Content-Type") || "";
  const total = Number(resp.headers.get("Content-Length") || 0);
  if (!resp.body || typeof resp.body.getReader !== "function") {
    return resp.blob();
  }
  const reader = resp.body.getReader();
  const chunks: BlobPart[] = [];
  let loaded = 0;
  let lastTick = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      loaded += value.byteLength;
      const now = Date.now();
      if (now - lastTick > 150) { lastTick = now; onProgress(loaded, total); }
    }
  }
  onProgress(loaded, total);
  return new Blob(chunks, { type });
}

export async function downloadChatAttachment(opts: {
  attachmentId?: number | null;
  url?: string | null;
  filename?: string | null;
  /** Размер файла, если известен: очень большие не тянем в память для share. */
  sizeBytes?: number | null;
}): Promise<void> {
  const id = opts.attachmentId ?? attachmentIdFromUrl(opts.url);
  const url = id != null
    ? chatAttachmentDownloadUrl(id)
    : (opts.url ? withDownloadFlag(opts.url) : null);
  if (!url) return;
  const filename = (opts.filename || "").trim() || `file-${id ?? "download"}`;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  const isMobile = window.innerWidth < 900;
  const canShareFiles = isMobile && typeof nav.canShare === "function" && typeof nav.share === "function";
  const tooBig = (opts.sizeBytes ?? 0) > SHARE_MAX_BYTES;
  if (!canShareFiles || tooBig) {
    anchorDownload(url, filename);
    return;
  }

  // Мобила: тянем файл с прогрессом, потом системный лист «Поделиться».
  const label = shortName(filename);
  const toast = showToast(`Скачиваю «${label}»…`);
  let blob: Blob;
  try {
    blob = await fetchWithProgress(url, (loaded, total) => {
      toast.update(total > 0
        ? `Скачиваю «${label}»… ${Math.min(100, Math.round((loaded / total) * 100))}% из ${fmtMb(total)}`
        : `Скачиваю «${label}»… ${fmtMb(loaded)}`);
    });
  } catch (err) {
    console.error("[download] fetch failed:", err);
    toast.update("Не удалось скачать файл. Попробуйте ещё раз.");
    toast.hide(3000);
    return;
  }

  const file = new File([blob], filename, { type: blob.type || "application/octet-stream" });
  try {
    if (nav.canShare({ files: [file] })) {
      toast.hide(0);
      await nav.share({ files: [file], title: filename });
      return;
    }
  } catch (err) {
    // AbortError — юзер сам закрыл share-лист; ничего больше не делаем.
    if ((err as { name?: string } | null)?.name === "AbortError") { toast.hide(0); return; }
    // NotAllowedError и прочее (например, истекла активация после долгой
    // загрузки) — сохраняем уже скачанный blob обычной ссылкой, без повторной
    // загрузки.
    console.warn("[download] Web Share failed, fallback to blob download:", err);
  }
  const objUrl = URL.createObjectURL(blob);
  anchorDownload(objUrl, filename);
  window.setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
  toast.update(`Сохраняю «${label}»…`);
  toast.hide(2000);
}
